import React from 'react';

import { useClassnames } from '@lumapps/classnames';
import { Instance } from '@lumapps/instance/types';
import { GenericEntityBlock } from '@lumapps/lumx-blocks/components/GenericEntityBlock';
import { mdiGoogleCirclesExtended } from '@lumapps/lumx/icons';
import { Size } from '@lumapps/lumx/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';

import { Community } from '../../types';
import { CommunityVisibility } from '../CommunityVisibility';

/**
 * Defines the props of the CommunityBlock.
 */
export type CommunityBlockProps = {
    /** Custom className */
    className?: string;
    /** Community object. */
    community: Partial<Pick<Community, 'id' | 'title' | 'description' | 'thumbnail' | 'privacy' | 'instance'>>;
    /** Provide instance name to display in additional fields. */
    instance?: {
        name: Instance['name'];
        title?: Instance['title'];
    };
    /** Block variant. */
    variant?: 'small' | 'regular';
    /** whether the community block is disabled or not */
    isDisabled?: boolean;
    /** Optional override of the standard additional infos. */
    additionalInfoOverride?: React.ReactNode;
};

/**
 * The default class name and classes prefix for this component.
 */
export const CLASSNAME = 'community-block';

/**
 * Render a community information as an horizontal community block.
 *
 * @family Blocks
 * @return The component.
 */
export const CommunityBlock: React.FC<CommunityBlockProps> = ({
    className,
    community,
    instance,
    variant,
    isDisabled,
    additionalInfoOverride,
}) => {
    const { translate, translateAndReplace } = useTranslate();
    const { block, element } = useClassnames(CLASSNAME);
    const translatedTitle = translate(community.title) as string;
    const isSmallVariant = variant === 'small';

    const additionalInfo = [];

    if (!additionalInfoOverride) {
        if (community.privacy) {
            additionalInfo.push(
                <CommunityVisibility
                    className={element('visibility')}
                    key="visibility"
                    visibility={community.privacy}
                />,
            );
        }

        if (instance?.name) {
            additionalInfo.push(
                <GenericEntityBlock.Attribute
                    key="instance"
                    value={translateAndReplace(GLOBAL.FORMAT_COLON, {
                        key: translate(GLOBAL.SOURCE) || '',
                        value: translate(instance.title) || instance.name,
                    })}
                />,
            );
        }
    }

    return (
        <GenericEntityBlock
            className={block([className])}
            title={translatedTitle}
            isDisabled={isDisabled}
            shouldCenterTitle={isSmallVariant}
            metadata={
                !isSmallVariant &&
                community.description && [
                    <GenericEntityBlock.Attribute key="description" value={translate(community.description) || ''} />,
                ]
            }
            thumbnail={{
                image: community.thumbnail,
                alt: translatedTitle,
                icon: mdiGoogleCirclesExtended,
                size: isSmallVariant ? Size.m : Size.l,
            }}
            additionalInfo={additionalInfoOverride || additionalInfo}
        />
    );
};

CommunityBlock.displayName = 'CommunityBlock';
